import { Box, Button, ButtonProps, CircularProgress, Grid, IconButton, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import PageHeader from "../../../materio/@core/components/page-header";
import SimpleDialog from "../../common/SimpleDialog";
import CustomChip from 'src/materio/@core/components/mui/chip'
import DeleteOutline from 'mdi-material-ui/DeleteOutline'
import EyeOutline from 'mdi-material-ui/EyeOutline'
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { CadenceAttachment, CadenceTemplate, loadPlaybooks, PlaybookStore } from "../PlaybookStore";
import { ChangeEvent, ElementType, useState } from "react";
import { client } from "../../../utils/axios-client";
import { ApiResponseType } from "../../../types";
import { Link } from 'react-router-dom';
import Switch from '@mui/material/Switch';
import DeleteIcon from '@mui/icons-material/Delete';
import useAwaitableComponent from '../../../hooks/useAwaitableComponent'
import ConfirmationDialog from '../../common/ConfirmationDialog'
import ApplicationStore from '../../../stores/ApplicationStore'
import RenderIf, { ClaimType, ClaimValue } from "../../common/RenderIf";
import UXButton from "../../../components/froms/UXButton";
import CloseIcon from '@mui/icons-material/Close';

const ImgStyled = styled('img')(({ theme }) => ({
    width: 120,
    height: 120,
    marginRight: theme.spacing(6.25),
    borderRadius: theme.shape.borderRadius
}))

const ButtonStyled = styled(Button)<ButtonProps & { component?: ElementType; htmlFor?: string }>(({ theme }) => ({
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        textAlign: 'center'
    }
}))

const ResetButtonStyled = styled(Button)<ButtonProps>(({ theme }) => ({
    marginLeft: theme.spacing(4.5),
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        marginLeft: 0,
        textAlign: 'center',
        marginTop: theme.spacing(4)
    }
}))

type Props = {
    open: boolean,
    onClose: () => void
}

const AttachmentVariantList = ({ open, onClose }: Props) => {
    const template = PlaybookStore.useState(s => s.selectedTemplateVariant)
    const [uploading, setUploading] = useState(false);
    const [status, execute, resolve, reject, reset] = useAwaitableComponent();
    const showModal = status !== "idle";

    const handleConfirmDelete = async (attachmentId: string) => {
        try {
            const value = await execute();
            const response = await client.delete<ApiResponseType>('/api/playbooks/templates/attachments', { params: { attachmentId: attachmentId } });

            if (response.data.succeeded) {
                await loadPlaybooks('');

                ApplicationStore.update(s => {
                    s.snackbarMessage = "Attachment successfully deleted";
                    s.snackbarOpen = true;
                });
            } else {
                alert('Error while deleting attachment.');
            }
        } catch (err) {

        } finally {
            reset();
        }
    };

    const onView = async (row: CadenceAttachment) => {

        //PlaybookStore.update(s => {
        //    s.selectedLibrary = response.data;
        //})

        //setViewMode(true);
        //setEditLibraryOpen(true);
    }

    const onFileChanged = async (file: ChangeEvent) => {
        const formData = new FormData();
        const { files } = file.target as HTMLInputElement
        if (files && template) {
            setUploading(true);
            for (var i = 0; i < files.length; i++) {
                if (files[i].type === 'application/pdf') {
                    formData.append('Files', files[i]);
                }
            }
            formData.append("templateId", template.id);

            const response = await client.post<ApiResponseType>('/api/playbooks/templates/attachments', formData);

            if (response.data.succeeded) {
                await loadPlaybooks('');
            } else {

            }            
        }

        setUploading(false);
    }

    function humanFileSize(bytes: number, si = false, dp = 1) {
        const thresh = si ? 1000 : 1024;

        if (Math.abs(bytes) < thresh) {
            return bytes + ' B';
        }

        const units = si
            ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
            : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
        let u = -1;
        const r = 10 ** dp;

        do {
            bytes /= thresh;
            ++u;
        } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);


        return bytes.toFixed(dp) + ' ' + units[u];
    }

    const toggleStatus = async (attachmentId: string) => {
        const response = await client.post<ApiResponseType>('/api/playbooks/templates/attachments/toggle/status', { id: attachmentId });

        if (response.data.succeeded) {
            await loadPlaybooks('');
        } else {

        }
    }

    return (
        <SimpleDialog open={open} onClose={onClose} sx={{ minHeight: 500 }}>
            <Grid container spacing={6} rowGap={3}>
                <Grid item xs={6}>
                    <PageHeader
                        title={<Typography variant='h5'>Attachments</Typography>}
                        subtitle={
                            <Typography variant='body2'>
                                Add and delete cadence attachments.
                            </Typography>
                        }
                    />
                </Grid>
                <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <Box>
                            <ButtonStyled component='label' variant='contained' htmlFor='pdf-upload' endIcon={!uploading && <FileUploadIcon />}>
                                {!uploading && 'Upload PDF'}
                                {uploading && 'Uploading'}
                                <input
                                    hidden
                                    type='file'
                                    onChange={onFileChanged}
                                    accept='application/pdf'
                                    id="pdf-upload"
                                />
                                {uploading && <CircularProgress style={{ color: 'white', marginLeft: 4 }} size={14} />}
                            </ButtonStyled>
                                                        
                            <UXButton sx={{ ml: 2 }} title="Close" onClick={onClose} icon={<CloseIcon />} >
                                Close
                            </UXButton>
                        </Box>
                    </Box>
                </Grid>
                <Grid xs={12}>
                    <TableContainer>
                        <Table size='small' sx={{ minWidth: 500 }}>
                            <TableHead
                                sx={{ backgroundColor: theme => (theme.palette.mode === 'light' ? 'grey.50' : 'background.default') }}
                            >
                                <TableRow>
                                    <TableCell sx={{ height: '3.375rem' }}>Document Name</TableCell>
                                    <TableCell sx={{ height: '3.375rem' }}>Content Type</TableCell>
                                    <TableCell sx={{ height: '3.375rem' }}>File Size</TableCell>                                    
                                    <TableCell sx={{ height: '3.375rem', textAlign: 'center' }}></TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {template?.attachments.map((item, index: number) => (
                                    <TableRow hover key={index} sx={{ '&:last-of-type td': { border: 0 } }}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                    <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>{item.documentName}</Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                    <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>{item.contentType}</Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                    <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>{humanFileSize(item.fileSize)}</Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>                                        
                                        <TableCell sx={{ width: 100, textAlign: 'center' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <RenderIf claimType={ClaimType.Playbooks} claimValue={ClaimValue.DeleteTemplateVariantFilters}>
                                                    <IconButton onClick={() => handleConfirmDelete(item.id)} title="Delete Attachment">
                                                        <DeleteIcon fontSize='small' />
                                                    </IconButton>
                                                </RenderIf>
                                                <Tooltip title='View'>
                                                    <a href={`/api/playbooks/templates/attachments/${template.id}/${item.id}`} download>
                                                        <Box>
                                                            <IconButton size='small' component='a' sx={{ textDecoration: 'none' }}>
                                                                <EyeOutline fontSize='small' />
                                                            </IconButton>
                                                        </Box>
                                                    </a>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {template?.attachments.length === 0 && <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center' }}>No Attachments</TableCell></TableRow>}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
            <ConfirmationDialog open={showModal} onConfirm={resolve} onCancel={reject} title="Delete?" message="Are you sure you want to delete the attachment?" confirmButtonText="Delete" cancelButtonText="Cancel" />
        </SimpleDialog>
    )
}

export default AttachmentVariantList;